import { useEffect } from "react";
import { useFormContext } from "react-hook-form";

import { useCrefazCltCities } from "@/app/hooks/useCrefazCltCities";
import { useDebounce } from "@/app/hooks/useDebounce";
import { BasicInput } from "@/view/components/BasicInput";

const normalize = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

type CityInputProps = {
  name: string;
  idName: string;
  label: string;
  uf?: string;
  error?: string;
};

export default function CityInput({
  name,
  idName,
  label,
  uf,
  error,
}: CityInputProps) {
  const { setValue, watch } = useFormContext();

  const typed = (watch(name) as string) ?? "";
  const debouncedName = useDebounce(500, typed);
  const { cities, isFetching } = useCrefazCltCities(uf, debouncedName);

  const isSearching = isFetching || debouncedName !== typed;

  useEffect(() => {
    const search = debouncedName?.trim() ?? "";

    const match =
      cities.find((city) => normalize(city.cidadeNome) === normalize(search)) ??
      (cities.length === 1 ? cities[0] : undefined);

    setValue(idName, match ? String(match.cidadeId) : "", {
      shouldValidate: false,
    });
  }, [cities, debouncedName, idName, setValue]);

  return (
    <div className="flex w-full flex-col">
      <BasicInput
        name={name}
        label={label}
        disabled={!uf}
        placeholder={uf ? "Digite a cidade" : "Selecione a UF primeiro"}
        error={isSearching ? undefined : error}
      />

      {isSearching && (
        <span className="mt-1 text-sm text-black04">Buscando cidade...</span>
      )}
    </div>
  );
}
